import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import CreateEventModal from './CreateEventModal';

function EventList({ events, onDelete, user }) {
  const navigate = useNavigate();
  const [editedState, setEditedState] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    event_name: '',
    event_date: '',
    target_participants: 0
  });
  const [creating, setCreating] = useState(false);
  const [areas, setAreas] = useState([]);
  const [areaTargets, setAreaTargets] = useState({});

  useEffect(() => {
    if (showModal && areas.length === 0) {
      loadAreas();
    }
  }, [showModal]);

  async function loadAreas() {
    try {
      const res = await api.getAreas();
      if (res.success && Array.isArray(res.areas)) {
        setAreas(res.areas);
        const initialTargets = {};
        res.areas.forEach(area => {
          initialTargets[area.id] = 0;
        });
        setAreaTargets(initialTargets);
      }
    } catch (err) {
      console.error('Error loading areas:', err);
    }
  }

  function handleChange(eventId, newStatus) {
    setEditedState({ ...editedState, [eventId]: newStatus });
  }

  async function handleSave(eventId) {
    const newStatus = editedState[eventId];
    
    // ✅ Conferma se sta cancellando
    if (newStatus === 'cancellato') {
      const confirmed = window.confirm(
        '⚠️ Stai per cancellare questo evento.\n\n' +
        'L\'evento resterà visibile ma sarà contrassegnato come CANCELLATO.\n\n' +
        'Continuare?'
      );
      if (!confirmed) {
        const newEdited = { ...editedState };
        delete newEdited[eventId];
        setEditedState(newEdited);
        return;
      }
    }
    

    
    try {
      let res;
      
      // ✅ Se lo stato è "cancellato", usa soft delete
      if (newStatus === 'cancellato') {
        console.log('🗑️ Soft deleting event:', eventId);
        res = await api.softDeleteEvent(eventId);
      } else {
        console.log('📝 Updating event status:', eventId, newStatus);
        res = await api.updateEventStatus(eventId, newStatus);
      }
      
      console.log('🔍 Save response:', res);
      
      if (res.success) {
        const newEdited = { ...editedState };
        delete newEdited[eventId];
        setEditedState(newEdited);
        onDelete(); // Ricarica eventi
      } else {
        alert('Errore aggiornamento stato: ' + (res.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('❌ Save error:', err);
      alert('Errore di connessione: ' + err.message);
    }
  }

  async function handleDelete(eventId) {
    if (!window.confirm('Eliminare DEFINITIVAMENTE questo evento?\n\nQuesta azione è irreversibile!')) return;
    
    console.log('🗑️ Hard deleting event:', eventId);
    
    try {
      const res = await api.deleteEvent(eventId);
      console.log('🔍 Delete response:', res);
      
      if (res.success) {
        onDelete();
      } else {
        alert('Errore eliminazione evento: ' + (res.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('❌ Delete exception:', err);
      alert('Errore di connessione: ' + err.message);
    }
  }


  return (
    <div className="container-fluid py-4">
      {/* HEADER */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <button 
              className="btn btn-outline-secondary"
              onClick={() => navigate('/tools')}
            >
              <i className="bi bi-arrow-left"></i> Torna al Menu
            </button>
            <h2 className="mb-0">Gestione Eventi</h2>
            {user.role === 'admin' && (
              <button 
                className="btn btn-success"
                onClick={() => setShowModal(true)}
              >
                <i className="bi bi-plus-circle"></i> Nuovo Evento
              </button>
            )}
            {user.role !== 'admin' && <div style={{ width: '150px' }}></div>}
          </div>
        </div>
      </div>
      {showModal && (
        <CreateEventModal
          onCreated={() => {
            setShowModal(false);
            onDelete(); // ricarica la lista eventi
          }}
          onClose={() => setShowModal(false)}
        />
      )}
      {/* TABELLA EVENTI */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead className="table-primary">
                <tr>
                  <th>Event</th>
                  <th>Data</th>
                  <th>Stato</th>
                  <th>Invitati</th>
                  <th>Confermati</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">
                      Nessun evento
                    </td>
                  </tr>
                ) : (
                  events.map(ev => (
                    <tr 
                      key={ev.id}
                      className={ev.status === 'cancellato' ? 'table-warning' : ''}
                      style={ev.status === 'cancellato' ? { opacity: 0.7 } : {}}
                    >
                      <td>
                        <strong>{ev.event_name}</strong>
                        {ev.status === 'cancellato' && (
                          <span className="badge bg-danger ms-2">CANCELLATO</span>
                        )}
                      </td>
                      <td>{new Date(ev.event_date).toLocaleDateString('it-IT')}</td>
                      <td>
                        {user.role === "admin" ? (
                          <div className="d-flex gap-2 align-items-center">
                            <select
                              className="form-select form-select-sm"
                              style={{ width: '150px' }}
                              value={editedState[ev.id] || ev.status}
                              onChange={e => handleChange(ev.id, e.target.value)}
                              disabled={ev.status === 'cancellato'}
                            >
                              <option value="pianificato">Pianificato</option>
                              <option value="confermato">Confermato</option>
                              <option value="concluso">Concluso</option>
                              <option value="cancellato">Cancellato</option>
                            </select>
                            {editedState[ev.id] && editedState[ev.id] !== ev.status && (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleSave(ev.id)}
                              >
                                Salva
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className={`badge ${
                            ev.status === 'cancellato' ? 'bg-danger' :
                            ev.status === 'confermato' ? 'bg-success' :
                            ev.status === 'concluso' ? 'bg-secondary' :
                            'bg-info'
                          }`}>
                            {ev.status.toUpperCase()}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-warning">{ev.invited_count || 0}</span>
                      </td>
                      <td>
                        <span className="badge bg-success">{ev.confirmed_count || 0}</span>
                      </td>
                      <td>
                        <button
                          className="btn btn-info btn-sm"
                          onClick={() => navigate(`/event-manager/${ev.id}`)}
                          disabled={ev.status === 'cancellato'}
                        >
                          Dettaglio
                        </button>
                        {user.role === "admin" && ev.status !== 'cancellato' && (
                          <button
                            className="btn btn-danger btn-sm ms-2"
                            onClick={() => handleDelete(ev.id)}
                          >
                            Elimina
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
}

export default EventList;